<script context="module" lang="ts">
  export type Row = {
    id: string;
    html: string | null;
    image: string;
    productId: string;
    catalog_code_sort: string;
    price: number | AlternativePriceDisplay;
    listPrice: number | undefined;
  };
  export enum AlternativePriceDisplay {
    Unavailable = "U",
    Login = "L",
    UnavailableList = "UL",
  }
  export type Table = {
    id: number;
    name: string;
    rows: Writable<Row[]>;
    headers: Header<Row>[];
    tableLength: string;
    categoryId: string;
  };
  function getIdentiferFromSortDisplay(tableRes: TableResponse | TableCategoryGroupResponse, sortDisplayName: string) {
    let identifiers = tableRes.catalogEntryGroups[0].catalogEntryView[0].attributes;
    return (identifiers.find((id) => sortDisplayName === id.name)?.identifier ?? sortDisplayName) as keyof Row;
  }
  export function convertStaticResponseToTable(
    tableRes: TableResponse | TableCategoryGroupResponse,
    headers: Header<Row>[],
  ): Table[] {
    let isRTPServiceDown = tableRes.isRTPServiceDown;
    let flattenDynamicRows = (obj: CatalogEntryView) => {
      let val: Record<string, string> = {};
      obj.tableViewAttributes.forEach((attr) => {
        let sortKey = getIdentiferFromSortDisplay(tableRes, attr.name);
        val[sortKey] = attr.values[0] ?? "";
      });
      return val;
    };
    return tableRes.catalogEntryGroups.map((x, i) => {
      return {
        id: i,
        name: x.groupName ?? "Additional Options",
        rows: writable(
          x.catalogEntryView.map((entry, index) => {
            return {
              id: entry.partNumber,
              html: null,
              image: entry.fullImage?.replace(/\/$/, "").split("/").pop() + "",
              productId: entry.partNumber,
              catalog_code_sort: entry.mfPartNumber_ntk,
              price: determinePrice(entry),
              listPrice: entry.price.find((price) => price.description === "L")?.value, //default price
              ...flattenDynamicRows(entry),
            };
          }),
        ),
        headers: headers,
        tableLength: x.recordSetTotal,
        categoryId: tableRes.categoryId,
      };
    });

    function determinePrice(entry: CatalogEntryView): Row["price"] {
      let cookiedUser = window.cookiedUser === "true";
      let isExpiredUser = cookiedUser && window.storeUserType === "G";
      let isLoginToViewPriceEnabled =
        entry.attributes.find((attr) => attr.identifier === "LoginForPrice")?.values[0].value === "1";
      let isRTPPriceAvailable = entry.priceDetailsView?.length > 0;
      let rtpPrice = isRTPPriceAvailable ? +entry.priceDetailsView?.[0].displayPrice : null;
      let listPrice = entry.price.find((price) => price.description === "L")?.value!;
      let isCustomer = window.storeUserType === "R" || isExpiredUser;

      if (isLoginToViewPriceEnabled) {
        if (isRTPServiceDown && isCustomer) {
          return isRTPPriceAvailable ? AlternativePriceDisplay.UnavailableList : AlternativePriceDisplay.Unavailable;
        }
        return isCustomer ? rtpPrice ?? listPrice : AlternativePriceDisplay.Login;
      } else {
        if (isCustomer) {
          if (isRTPServiceDown) return AlternativePriceDisplay.UnavailableList;
          return rtpPrice && isRTPPriceAvailable ? rtpPrice : listPrice;
        }
        return listPrice;
      }
    }
  }
</script>

<script lang="ts">
  import { type Writable, writable } from "svelte/store";
  import { onMount, setContext, tick } from "svelte";
  import CatagoryTable from "./lib/CatagoryTable.svelte";
  import intersect from "./lib/intersect";
  import { type Header } from "./lib/DataTable.svelte";
  import { type TableCategoryGroupResponse, type CatalogEntryView, type TableResponse } from "./lib/api/category.types";
  import Select from "./lib/Select.svelte";
  import SkeletonCard from "./lib/SkeletonCard.svelte";
  import { getHtmlByPartNumber, getListHtmlGroup, getFacetParams } from "./lib/api/category";

  export let staticTableResponse: TableResponse;
  let activeHeaderParam: Writable<{ orderBy: string; direction: string } | null> = setContext(
    "activeHeaderParam",
    writable(null),
  );
  export let headers: Header<Row>[] = createHeaders();

  function createHeaders(): Header<Row>[] {
    const guaranteedHeaders: Header<Row>[] = [
      { display: "", key: "image" },
      { display: "Part", key: "productId", sort: true },
      { display: "Mfg #", key: "catalog_code_sort", sort: true },
    ];

    const otherHeaders: Header<Row>[] = staticTableResponse.tableViewHeaders.map((sortDisplayName) => ({
      display: sortDisplayName,
      key: getIdentiferFromSortDisplay(staticTableResponse, sortDisplayName),
      sort: true, // Enable sorting for all headers
    }));

    const endHeaders: Header<Row>[] = [{ display: "Price", key: "price", sort: true }];
    const allHeaders = [...guaranteedHeaders, ...otherHeaders, ...endHeaders];
    return allHeaders;
  }

  let categories = convertStaticResponseToTable(staticTableResponse, headers);

  let sliceCount = 1;
  let maxCategoryCount = categories.length;
  //we only need ref to first element but wean only bind to
  //an identifier (e.g. `foo`) or a member expression (e.g. `foo.bar` or `foo[baz]`)
  //so we attach all to a ref and only refer to [0] for first ref to measure
  let tableRefArray: HTMLElement[] = [];

  let containerRef: HTMLElement;
  let categoryHeight = 0;

  let isMobile = false;

  async function handleRowExpanded(event: CustomEvent<{ id: string; index: number; partNumber: string }>) {
    const { id, index, partNumber } = event.detail;
    let htmlContent = await fetchHtmlData(partNumber);
    if (isIfAlreadyInCompare()) {
      //toggle check if compare is already toggled
      htmlContent = htmlContent.replace(/class="item-compare-checkbox jq-compare-item"/g, "input checked");
    }
    categories[index].rows.update((rows) => rows.map((row) => (row.id === id ? { ...row, html: htmlContent } : row)));

    //---
    function isIfAlreadyInCompare(): boolean {
      const imageUrls = document.querySelectorAll(".compare-floating-widget__items img");
      const ids = Array.from(imageUrls)
        .map((img) => {
          const url = img.src;
          // Extract the number before '_' in the URL path
          const match = url.match(/\/(\d+)_/);
          return match ? match[1] : null;
        })
        .filter((id) => id !== null);

      return ids.includes(partNumber.substring(1) + "");
    }
    return htmlContent;
  }

  async function fetchHtmlData(partNumber: string): Promise<string> {
    try {
      const response = await getHtmlByPartNumber({ partNumber });

      return response;
    } catch (error) {
      console.error("Error fetching HTML data:", error);
      throw error;
    }
  }

  function calculateCategoriesToLoad(availableHeight: number): number {
    if (categoryHeight === 0) return 1; // If we haven't measured yet, just load one category
    return Math.ceil(availableHeight / categoryHeight);
  }

  function loadMoreCategories() {
    const categoriesToLoad = calculateCategoriesToLoad(availableHeight);
    sliceCount = Math.min(sliceCount + categoriesToLoad, maxCategoryCount);
  }

  function handleResize() {
    isMobile = window.matchMedia("(max-width: 768px)").matches;
    const categoriesToLoad = calculateCategoriesToLoad(availableHeight);
    if (categoriesToLoad > sliceCount) {
      loadMoreCategories();
    }
  }

  function intersectionCallback() {
    if (sliceCount < maxCategoryCount) {
      loadMoreCategories();
    }
  }

  onMount(async () => {
    // Wait for the DOM to update and measure the first category
    handleResize();
    await tick();
    let firstTableRef = tableRefArray[0];
    if (firstTableRef) {
      categoryHeight = firstTableRef.offsetHeight;
      loadMoreCategories();
    }
  });
  $: availableHeight = containerRef && window.innerHeight - containerRef.getBoundingClientRect().top;

  // ------------------------------- MOBILE SECTION --------------------------
  // Select category script section
  let selectData: {
    key: string;
    display: string;
  }[] = staticTableResponse.catalogEntryGroups.map((group, index) => ({
    key: index + "",
    display: `${group.groupName ?? "Additional Options"} (${group.recordSetTotal} items)`,
  }));

  let loadedMobileHtml = ``;
  let selectedValue = selectData[0];
  let placeholderText = "Select a Category";
  let labelText = "Group " + 1 + " of " + staticTableResponse.catalogEntryGroups.length;
  let mobilePage: number;
  let mobileLoad = false;
  let toggleLoadMore = false;

  function getSelectValueGroupName(selected: { key: string; display: string }): string | null {
    const indexOfOpenParen = selected.display.indexOf("(");
    let groupName: string | null = selected.display.substring(0, indexOfOpenParen).trim();
    if (groupName == "Additional Options") {
      groupName = null;
    }
    return groupName;
  }

  function getSelectValueSetTotal(selected: { key: string; display: string }): string {
    const indexOfOpenParen = selected.display.indexOf("(");
    const indexOfCloseParen = selected.display.indexOf(")") - 6;
    const recordSetTotal = selected.display.substring(indexOfOpenParen + 1, indexOfCloseParen);
    return recordSetTotal;
  }

  function handleOptionClick(event: CustomEvent<string>) {
    //scrolls to the top of the page when option click, 320px down from the top
    setTimeout(() => {
      const targetElement = document.getElementById("top");
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100); // the delay ensures all content is loaded, which prevents bug where it won't scroll to the top
    const key = event.detail;
    let index = selectData.findIndex((item) => item.key === key);
    selectedValue = selectData[index] || selectData[0];
    labelText = "Group " + (index + 1) + " of " + staticTableResponse.catalogEntryGroups.length;
    const groupName = getSelectValueGroupName(selectedValue);
    const recordSetTotal = getSelectValueSetTotal(selectedValue);

    window.digitalData.event.push({
      name: "product_list_page",
      data: {
        methodDetails: groupName, //ex: Aluminum
        methodName: "product list page:group filter",
        TotalProducts: recordSetTotal, //ex:30
      },
    });
  }

  // Function to get content based on selected value
  async function getContentForSelectedValue(value: { key: string; display: string }): Promise<void> {
    const FETCH_COUNT = 24;
    if (mobileLoad) return;
    mobileLoad = true;
    const groupName = getSelectValueGroupName(value);
    const recordTotal = getSelectValueSetTotal(value);

    toggleLoadMore = +recordTotal > FETCH_COUNT;

    mobilePage = 1; // Reset page count when changing category
    const response = await getListHtmlGroup({
      categoryId: staticTableResponse.categoryId,
      groupName: groupName,
      resultsPerPage: FETCH_COUNT,
      beginIndex: 0,
      facets: getFacetParams(),
    });
    loadedMobileHtml = response; // Set initial content as a string
    mobileLoad = false;
  }

  function isEmptyCategoryPage(content: string): boolean {
    return content.includes('class="category-page__no-results"') && content.includes("???CATEGORY_EMPTY_RESULTS???");
  }

  async function loadMoreMobileHtml(value: { key: string; display: string }): Promise<boolean> {
    const FETCH_COUNT = 24;
    const groupName = getSelectValueGroupName(value);
    const recordTotal = getSelectValueSetTotal(value);

    toggleLoadMore = +recordTotal > FETCH_COUNT * mobilePage;

    let newContent;
    if (toggleLoadMore) {
      newContent = await getListHtmlGroup({
        categoryId: staticTableResponse.categoryId,
        groupName: groupName,
        resultsPerPage: FETCH_COUNT,
        beginIndex: FETCH_COUNT * mobilePage,
        facets: getFacetParams(),
      });

      if (isEmptyCategoryPage(newContent)) {
        toggleLoadMore = false;
        return false;
      }

      // Check if the new content is already present in loadedMobileHtml
      if (!loadedMobileHtml.includes(newContent)) {
        loadedMobileHtml += newContent;
        //loadedMobileHtml = loadedMobileHtml.replaceAll("data-src=", "src=");
        // ^ this hack works but goutham suggested a better way...
        //find the element by name if it has data-src but not src take whats after
        //and use that as src and visa verse for src but no data-src
        mobilePage++;
        return true;
      }
    }

    // If content is duplicate, don't add it and return false
    return false;
  }

  async function mobileIntersectionCallBack(): Promise<void> {
    if (toggleLoadMore) {
      mobileLoad = true;
      await loadMoreMobileHtml(selectedValue);
      mobileLoad = false;
    }
  }

  let skeletonCards = 5;
  let skeletonArr: any = [];
  const init = () => {
    for (let i = 0; i < skeletonCards; i++) {
      skeletonArr.push(i);
    }
  };
  init();

  //dynamic sticky header
  let isStuck = false; //tracks if the header is in the stuck state

  // svelte action runs when the element is created
  function stickyAction(node: HTMLElement) {
    // entries contains intersection changes, entry.isIntersecting: false when sentinel is out of viewport
    // boundingClientRect.y < 0: ensures we've scrolled past the observing div
    // dispatches custom "stuck" event when state changes
    const intersectionCallback = function (entries: IntersectionObserverEntry[]) {
      const entry = entries[0];
      isStuck = !entry.isIntersecting && entry.boundingClientRect.y < 0;
      node.dispatchEvent(
        new CustomEvent("stuck", {
          detail: { isStuck },
        }),
      );
    };

    // starts watching
    const intersectionObserver = new IntersectionObserver(intersectionCallback, {});
    intersectionObserver.observe(node);

    return {
      // cleans up both observers
      destroy() {
        intersectionObserver.disconnect();
      },
    };
  }
</script>

<svelte:window on:resize={handleResize} />
<!-- svelte-ignore a11y-click-events-have-key-events -->
<div bind:this={containerRef}>
  {#if !isMobile}
    {#each categories.slice(0, sliceCount) as item, index (item.id)}
      <div bind:this={tableRefArray[index]}>
        <div
          id={item.name}
          class="font-montserrat mt-[24px] text-left text-[16px] font-semibold normal-case leading-normal tracking-[-0.4px] text-[#292929]"
        >
          {@html item.name}
        </div>
        <div class="mb-[24px] mt-[10px] flex items-end">
          <div class="h-[7px] w-[32px] bg-[#ffcc33]" />
          <div class="h-[2px] flex-grow bg-[#ededed]" />
        </div>
        <CatagoryTable
          tableLength={+item.tableLength}
          headers={item.headers}
          groupName={item.name}
          categoryId={item.categoryId}
          bind:rows={item.rows}
          on:rowExpanded={handleRowExpanded}
          {index}
        />
      </div>
    {/each}
    {#if sliceCount < maxCategoryCount}
      <div class="flex justify-center text-[#5b5b5b]">Loading...</div>
    {/if}
  {:else}
    <div id="top" />
    <div
      class="font-Montserrat text-16 mb-1 pl-[16px] text-left font-semibold leading-normal tracking-normal text-black"
    >
      Browse By Groups:
    </div>
    <div style="position: absolute; height: 1px;" use:stickyAction on:stuck={(e) => (isStuck = e.detail.isStuck)} />
    <div class:isStuck class="sticky top-0 px-4" style="z-index: 9;">
      <Select
        data={selectData}
        bind:value={selectedValue}
        placeholder={placeholderText}
        label={labelText}
        on:optionClick={handleOptionClick}
        on:click={(e) => {
          if (e.target.classList.contains("input-cell")) return;
          window.digitalData.event.push({
            name: "product_list_page",
            data: {
              methodDetails: "open",
              methodName: "product list page:group filter",
            },
          });
        }}
      />
    </div>
    {#await getContentForSelectedValue(selectedValue)}
      {#each skeletonArr as _}
        <SkeletonCard />
      {/each}
    {:then}
      {@html loadedMobileHtml}
      {#if mobileLoad}
        {#each skeletonArr as _}
          <SkeletonCard />
        {/each}
      {/if}
    {:catch error}
      <div class="flex justify-center py-5">Error: {error.message}</div>
    {/await}
    <div style="height:5px;" id="bottom" use:intersect={mobileIntersectionCallBack} />
    {#if selectData.filter((item) => item.key !== selectedValue.key).length != 0}
      <div class="select-next-container">
        <div class="flex h-[24px] justify-center bg-[#EDEDED] align-middle font-extrabold text-[#5b5b5b]">NEXT</div>
        {#each selectData.filter((item) => item.key !== selectedValue.key) as item}
          <a href="#top">
            <div
              class="cursor-pointer py-[8px] pl-[24px] hover:bg-[#EDEDED]"
              on:click={() => {
                selectedValue = item;
                const groupName = getSelectValueGroupName(selectedValue);
                const recordSetTotal = getSelectValueSetTotal(selectedValue);
                window.digitalData.event.push({
                  name: "product_list_page",
                  data: {
                    methodDetails: groupName, //ex: Aluminum
                    methodName: "product list page:next",
                    TotalProducts: recordSetTotal, //ex:30
                  },
                });
              }}
            >
              {@html item.display}
            </div>
          </a>
        {/each}
      </div>
    {/if}
  {/if}

  <div style="height:5px;" id="bottom" use:intersect={intersectionCallback} />
</div>

<style>
  :global(h1) {
    padding-bottom: 0 !important;
  }

  :global(.el--page-pad) {
    margin: 0 !important;
  }

  :global(.subcat-box__left) {
    padding: 0 16px 0 16px !important;
  }

  :global(.subcat-grid-tile-container) {
    padding: 0 16px;
  }

  :global(.subcat-grid-tile-container *) {
    z-index: 0 !important;
  }

  .select-next-container {
    border: 1px solid #979797;
    border-top: 2px solid #fcba12;
    margin: 15px 16px 0 16px;
  }

  :global(.subcat-grid-tile__image-container) {
    display: flex;
    justify-content: center;
  }

  .sticky {
    transition: padding 0.3s ease;
  }

  .sticky.isStuck {
    padding: unset;
    transition: padding 0.3s ease;
  }

  @media (max-width: 767px) {
    :global(.subcat-box__left .subcat-filters) {
      border-top: none;
      padding-top: unset;
    }
    :global(.subcat-box__left .subcat-filters > a) {
      border: none;
      text-transform: capitalize;
      font-size: 1.4rem;
      font-weight: 600;
      color: #3e64b6;
    }
    :global(.subcat-box .subcat-box__left) {
      flex: 0 0 content;
      min-width: unset;
      padding: 0 !important;
      margin-right: 16px;
      margin-top: 16px;
      top: 22px;
      position: relative;
      left: calc(100vw - 62px);
    }
    :global(.subcat-filters .subcat-filters__mobile-header) {
      margin: 0;
      padding: 0;
      text-align: left;
    }
    :global(.subcat-header__result-count-table-view) {
      font-weight: unset !important;
    }
  }
</style>
